import { Match2Reward } from '../match2/rewards/match2-rewards.ruleset';
import { getPuzzleWinsLast7Days } from '../puzzleWinCount/puzzleWinCount.getters';
import { State } from '../State';
import { assertNever } from '../utils';
import {
  treasureExpressUnlockLevel,
  treasureExpressMilestones,
  treasureExpressSchedule,
  treasureExpressTicketRewards,
  Difficulty,
  treasureExpressMilestoneRewards,
} from './treasureExpress.ruleset';

export function isTreasureExpressAvailable(state: State, now: number): boolean {
  // Not available if the player hasn't reached the required level
  if (state.match2.level < treasureExpressUnlockLevel) {
    return false;
  }

  // Should be available if there are unclaimed milestones
  if (getUnclaimedMilestones(state, now)) {
    return true;
  }

  // Not available if there is no schedule
  if (!treasureExpressSchedule.getCurrentEvent(now)) {
    return false;
  }

  // All checks passed, Treasure Express is available
  return true;
}

/**
 * Get unclaimed milestones.
 * @param state - The user state.
 * @param now - The current time.
 * @returns A record of the unclaimed milestones per event.
 */
export function getUnclaimedMilestones(
  state: State,
  now: number,
): Record<string, number> | undefined {
  // Return empty object if no entries exist
  if (!state.treasureExpress?.entries) {
    return undefined;
  }

  const unclaimedMilestones: Record<string, number> = {};
  for (const [eventId, event] of Object.entries(
    state.treasureExpress.entries,
  )) {
    // Adjust Milestones depending on User's difficulty
    const treasureExpressDifficultyMilestones = getEventMilestones(
      state,
      eventId,
    );

    // If all milestones are claimed, skip this event.
    if (
      event.claimedMilestones === treasureExpressDifficultyMilestones.length
    ) {
      continue;
    }

    // Check if the user has enough tickets to claim each milestone.
    const unclaimed = treasureExpressDifficultyMilestones.filter(
      (x) => event.tickets >= x,
    );

    // If the remaining claim count is 0, skip this event.
    const numUnclaimed = unclaimed.length - event.claimedMilestones;
    if (numUnclaimed <= 0) {
      continue;
    }

    // Only include events with unclaimed milestones
    unclaimedMilestones[eventId] = numUnclaimed;
  }

  // Return undefined if there are no unclaimed milestones, for practical reasons.
  if (Object.keys(unclaimedMilestones).length === 0) {
    return undefined;
  }

  // Also return undefined if they are all 0
  if (Object.values(unclaimedMilestones).every((x) => x === 0)) {
    return undefined;
  }

  return unclaimedMilestones;
}

/**
 * Get the current ticket count for an event running at a given timestamp.
 * @param state - The user state.
 * @param now - The current time.
 * @returns The number of accumulated tickets in the event.
 */
export function getEventTickets(state: State, eventId: string): number {
  const currentEvent = state.treasureExpress?.entries[eventId];
  if (!currentEvent) return 0;
  return currentEvent.tickets;
}

/**
 * Get the milestones for an event running at a given timestamp.
 * @param state - The user state.
 * @param now - The current time.
 * @returns The milestones of the event.
 */
export function getEventMilestones(state: State, eventId: string): number[] {
  const difficulty = getTreasureExpressDifficulty(state, eventId);
  return treasureExpressMilestones[difficulty];
}

/**
 * Get the number of completed milestones for a given event.
 * @param state
 * @param eventId
 * @returns
 */
export function getEventCompletedMilestones(
  state: State,
  eventId: string,
): number {
  const entry = state.treasureExpress?.entries[eventId];
  if (!entry) return 0;
  const milestones = getEventMilestones(state, eventId);
  const completedMilestones = milestones.filter(
    (x) => entry.tickets >= x,
  ).length;
  return completedMilestones;
}

/**
 * Retrieve the ongoing treasure express event.
 * @param state
 * @param now
 * @returns Ongoing event if available, undefined otherwise
 */
export function getOngoingTreasureExpressEvent(
  state: State,
  now: number,
):
  | {
      remainingTime: number;
      id: string;
      start: number;
      end: number;
      metadata?: undefined;
    }
  | undefined {
  if (!isTreasureExpressAvailable(state, now)) {
    return undefined;
  }

  const schedule = treasureExpressSchedule.getCurrentEvent(now);
  if (!schedule) {
    return undefined;
  }

  return {
    ...schedule,
    remainingTime: treasureExpressSchedule.getRemainingTime(now),
  };
}
/**
 * Retrieve remaining tickets needed for next milestone
 * @param state
 * @param eventId
 * @returns
 */
export function getTreasureExpressRemainingTickets(
  state: State,
  eventId: string,
): number {
  const entry = state.treasureExpress?.entries[eventId];
  // If there is no entry, return 0 tickets
  if (!entry) return 0;
  const milestones = getEventMilestones(state, eventId);
  const nextMilestone = milestones[getEventCompletedMilestones(state, eventId)];
  return nextMilestone - entry.tickets;
}

/**
 * Get the number of treasure express tickets for a given number of attempts
 * @param puzzleAttempts
 * @returns
 */
export function getTreasureExpressTicketsByAttempts(
  puzzleAttempts: number,
): number {
  const attempt = Math.min(
    puzzleAttempts,
    treasureExpressTicketRewards.length - 1,
  );
  const tickets = treasureExpressTicketRewards[attempt];
  return tickets;
}

/**
 * Retrieve the treasure express puzzle event properties for analytics
 * @param state
 * @param now
 * @param numAttempts
 * @returns
 */
export function getTreasureExpressPuzzleEventProperties(
  state: State,
  now: number,
  puzzleAttempts?: number,
): {} {
  const event = getOngoingTreasureExpressEvent(state, now);
  if (!event) return {};

  const common = {
    treasureExpressEventID: event.id,
    treasureExpressTicketsTotal: getEventTickets(state, event.id),
    treasureExpressStage: getEventCompletedMilestones(state, event.id),
    treasureExpressSegment: getTreasureExpressSegmentByDifficulty(
      getTreasureExpressDifficulty(state, event.id),
    ),
  };

  // If no puzzle attempts defined, return only the common properties
  if (puzzleAttempts === undefined) {
    return common;
  }

  // If puzzle attempts is defined, add the tickets gained to the retuning properties
  return {
    ...common,
    treasureExpressTicketsAdded:
      getTreasureExpressTicketsByAttempts(puzzleAttempts),
  };
}

/**
 * Retrieve the segment for the treasure express event based on difficulty
 * @param difficulty
 * @returns
 */
export function getTreasureExpressSegmentByDifficulty(
  difficulty: Difficulty,
): 1 | 3 | 2 {
  switch (difficulty) {
    case 'easy':
      return 1;
    case 'normal':
      return 2;
    case 'hard':
      return 3;
    default:
      assertNever(difficulty);
  }
}

/**
 * Determines the difficulty setting for a user based on their puzzle win history
 * over the past 7 days.
 *
 * @param state - The current state object containing user data and puzzle history.
 * @param now - The current timestamp used to calculate the 7-day window.
 * @returns The difficulty level as a string: 'easy', 'normal', or 'hard'.
 *
 * The function calculates the user's daily win rate average over the past 7 days
 * and assigns a difficulty level based on the following criteria:
 * - 'easy': Average daily wins <= 10
 * - 'normal': Average daily wins > 10 and <= 15
 * - 'hard': Average daily wins > 15
 */
export function getTreasureExpressDifficultySetting(
  state: State,
  now: number,
): Difficulty {
  // User's number of wins in the last 7 days
  const userWinsLast7Days = getPuzzleWinsLast7Days(state, now).reduce(
    (acc, val) => acc + val,
    0,
  );

  // Calculate the win rate average
  const dailyWinRateAverage = Math.round(userWinsLast7Days / 7);

  // Segment 1: players that in the week prior to feature start did a daily average of wins <=10
  // Segment 2: players that in the week prior to feature start did a daily average of wins >10
  // Segment 3: players that in the past week prior to feature start did a daily average of wins >15
  switch (true) {
    case dailyWinRateAverage <= 10:
      return 'easy';
    case dailyWinRateAverage > 10 && dailyWinRateAverage <= 15:
      return 'normal';
    default:
      return 'hard';
  }
}

/**
 * Get the difficulty of a specific event.
 */
export function getTreasureExpressDifficulty(
  state: State,
  eventId: string,
): Difficulty {
  if (!state.treasureExpress.entries[eventId]) {
    return 'normal';
  }

  return state.treasureExpress.entries[eventId].difficulty as Difficulty;
}

/**
 * Retrieves the rewards for a specific event based on its difficulty level.
 *
 * @param state - The current application state.
 * @param eventId - The unique identifier of the event.
 * @returns A two-dimensional array of `Match2Reward` objects representing the rewards
 *          for the event, categorized by difficulty.
 */
export function getTreasureExpressRewards(
  state: State,
  eventId: string,
): Match2Reward[][] {
  const difficulty = getTreasureExpressDifficulty(state, eventId);
  return treasureExpressMilestoneRewards[difficulty];
}
